import { Checkbox, Form, Input, InputNumber, Select, Spin } from 'antd';
import { FormInstance } from 'antd/lib/form/Form';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { useGetMappedEventsWithOutOfBox } from '@api/emp/queryHooks';
import { useGetMetricList } from '@src/emp-3.0/api/queryHooks';
import {
  ECalculationType,
  EMetricType,
  EOccurrenceType,
  EPairType,
  EWithinUnitType,
  IMetric,
} from '@src/emp-3.0/types';
import { buildOptionsWithOutOfBox } from '@src/emp-3.0/utils/buildOptions';
import {
  calculationOptions,
  defaultInitialValues,
  metricTypeOptions,
  occurrenceTypeOptions,
  pairingLogicOptions,
  withinUnitOptions,
} from '@src/pages/metric-templates/constants';

import styles from './index.less';
import { checkSuccessConversionWindow } from '@src/emp-3.0/utils/metrics';
import { EFormMode } from '@src/emp-3.0/types/common';

const { TextArea } = Input;

export function generateFormData(metricData: IMetric, mode: EFormMode = EFormMode.VIEW) {
  const values: any = {
    metricType: metricData?.metrics_type!,
    metricName: mode === EFormMode.CLONE ? `${metricData?.name!}_Copy` : metricData?.name!,
    description: metricData?.description!,
    calculation: metricData?.definition?.calculation || ECalculationType.CONVERSION_RATE,
    firstEvent: metricData?.definition?.first_event!,
    secondEvent: metricData?.definition?.second_event!,
    withinRange: metricData?.definition?.within_range!,
    withinUnit: metricData?.definition?.within_unit || EWithinUnitType.SECOND,
    pairingLogic: metricData?.definition?.pairing_logic || EPairType.FIRST_FIRST,
    occurrenceType: metricData?.definition?.occurrence_type || EOccurrenceType.SESSIONS,
    need_distribution: metricData?.definition?.need_distribution || false,
    distribution: {
      min_value: metricData?.definition?.distribution?.min_value ?? null,
      max_value: metricData?.definition?.distribution?.max_value ?? null,
    },
  };
  return values;
}

export const getViewData = (metric, eventData) => {
  let firstEventLabel = metric.first_event;
  let secondEventLabel = metric.second_event;
  if (Number(firstEventLabel)) {
    firstEventLabel = eventData.data?.find(
      (item) => item.event_id === metric.first_event,
    )?.event_name;
    secondEventLabel = eventData.data?.find(
      (item) => item.event_id === metric.second_event,
    )?.event_name;
  }

  return {
    metricTypeLabel: metricTypeOptions.find((x) => x.value === metric.metrics_type)?.label,
    calculationLabel: calculationOptions.find((x) => x.value === metric.calculation)?.label,
    withinRangeLabel: metric.within_range,
    withinUnitLabel: metric.within_unit,
    pairingLogicLabel: pairingLogicOptions.find((x) => x.value === metric.pairing_logic)?.label,
    secondEventLabel,
    firstEventLabel,
    occurrenceTypeLabel: occurrenceTypeOptions.find((x) => x.value === metric.occurrence_type)
      ?.label,
  };
};

export const validateMetricName = (rule, value, type = 'metric', checkEmpty = true) => {
  if (!checkEmpty && !value) {
    return Promise.resolve();
  }

  if (typeof value !== 'string' || value.length === 0) {
    const upperType = type.charAt(0).toUpperCase() + type.slice(1);
    return Promise.reject(`${upperType} name is required`);
  }
  const resp = /^[\w\s\-<>]+$/;
  if (!resp.test(value)) {
    return Promise.reject(
      `Only underscore and space as special characters are supported for ${type} name`,
    );
  }
  return Promise.resolve();
};

interface IProps {
  mode?: EFormMode;
  disabledList?: string[];
  onFormValueChange?: (value: any, form?: FormInstance) => void;
  firstEventStatus?: string;
  templateFirstEvent?: string;
  secondEventStatus?: string;
  templateSecondEvent?: string;
  defaultFormData?: any;
}

// Looks like this component is not being used.
// can be removed and validate in later release.
const MetricDetail = (props: IProps) => {
  const {
    mode = EFormMode.EDIT,
    disabledList = [],
    onFormValueChange,
    firstEventStatus,
    secondEventStatus,
    templateFirstEvent,
    templateSecondEvent,
    defaultFormData,
  } = props;
  const [form] = Form.useForm();

  const { metricId } = useParams<any>();
  const [viewData, setViewData] = useState({} as any);
  const { data } = useGetMappedEventsWithOutOfBox();
  const { data: userDefinedMetrics = [] } = useGetMetricList();
  const [forceRerender, setForceRerender] = useState(false);
  const metric = userDefinedMetrics.find((metric) => metric.id === Number(metricId));
  const { initialEventOptions, followUpEventOptions } = buildOptionsWithOutOfBox(data?.data);

  useEffect(() => {
    // view mode
    if (mode === 'view' && data) {
      if (metric) {
        const values = generateFormData({ ...metric });
        form.setFieldsValue({ ...values });
        const viewData = getViewData(
          {
            metrics_type: metric.metrics_type,
            ...metric.definition,
          },
          data,
        );
        setViewData(viewData);
      } else {
        const values = generateFormData({ ...defaultFormData });
        form.setFieldsValue({ ...values });
        const viewData = getViewData(
          {
            metrics_type: defaultFormData.metrics_type,
            ...defaultFormData.definition,
          },
          data,
        );
        setViewData(viewData);
      }

      setForceRerender(!forceRerender);
      return;
    }

    // edit mode
    if (!metric) {
      let formValues = {
        ...defaultInitialValues,
        calculation: ECalculationType.AVERAGE,
        occurrenceType: EOccurrenceType.FIRST_PAIR,
      };
      if (defaultFormData) {
        formValues = { ...defaultInitialValues, ...defaultFormData };
      }
      form.setFieldsValue(formValues);
      onFormValueChange?.(formValues, form);
      setForceRerender(!forceRerender);
      return;
    }
    const values = generateFormData({ ...metric }, mode);
    form.setFieldsValue({ ...values });
    onFormValueChange?.(values, form);
  }, [metric, mode, defaultFormData, data]);

  const validateFirstEvent = (rule, value) => {
    if (firstEventStatus) {
      if (firstEventStatus === 'is_defined') {
        return Promise.resolve();
      }
      if (typeof value !== 'number') {
        return Promise.reject('Initial event is required');
      }
      if (firstEventStatus === 'alternative' && secondEventStatus === 'is_defined') {
        const firstEvent = data?.data?.find((item) => item.event_id === value)?.event_name;
        if (firstEvent === templateSecondEvent) {
          return Promise.reject('Initial event is the same with complete event');
        }
      }
    } else {
      if (typeof value !== 'number') {
        return Promise.reject('Initial event is required');
      }
    }
    return Promise.resolve();
  };

  const validateSecondEvent = (rule, value) => {
    if (secondEventStatus) {
      if (secondEventStatus === 'is_defined') {
        return Promise.resolve();
      }
      if (typeof value !== 'number') {
        return Promise.reject('Follow-up event is required');
      }
      if (secondEventStatus === 'alternative' && firstEventStatus === 'is_defined') {
        const secondEvent = data?.data?.find((item) => item.event_id === value)?.event_name;
        if (secondEvent === templateFirstEvent) {
          return Promise.reject('Follow-up event is the same with initial event');
        }
      }
      const firstEvent = form?.getFieldValue('firstEvent');
      if (
        secondEventStatus === 'alternative' &&
        firstEventStatus === 'alternative' &&
        value === firstEvent
      ) {
        return Promise.reject('Follow-up event is the same with initial event');
      }
    } else {
      const secondEvent = value;
      const firstEvent = form?.getFieldValue('firstEvent');
      if (typeof secondEvent !== 'number') {
        return Promise.reject('Follow-up event is required');
      }
      if (firstEvent === secondEvent) {
        return Promise.reject('Follow-up event is the same with initial event');
      }
    }
    return Promise.resolve();
  };

  const validateTime = (rule, value) => {
    const withinUnit = form?.getFieldValue('withinUnit') as EWithinUnitType;
    if (typeof withinUnit !== 'string') {
      return Promise.reject('Within unit is required');
    }
    if (typeof value !== 'number') {
      return Promise.reject('Within time is required');
    }
    return checkSuccessConversionWindow(withinUnit, value);
  };

  const renderEventAlert = (status: string | undefined, type: 'first' | 'second') => {
    const text = type === 'first' ? templateFirstEvent : templateSecondEvent;
    if (status === 'template') {
      return (
        <span className={styles.alternativeAlert}>
          {`Please select an event representing "${text}"`}
        </span>
      );
    }
    return null;
  };

  const renderEventItem = (status: string | undefined, type: 'first' | 'second') => {
    const text = type === 'first' ? templateFirstEvent : templateSecondEvent;
    const options = type === 'first' ? initialEventOptions : followUpEventOptions;
    if (status === 'template_defined') {
      return <span className={styles.definedEvent}>{text}</span>;
    }
    return (
      <Select
        placeholder="Select an event"
        options={options}
        style={{ width: 'fit-content', minWidth: '250px', marginBottom: '5px' }}
        dropdownStyle={{ minWidth: '350px' }}
        showSearch
        filterOption={(input, option) =>
          ((option?.label ?? '') as string).toLowerCase().includes(input.toLowerCase())
        }
        disabled={disabledList.includes(`${type}Event`)}
      />
    );
  };

  const renderDistribution = () => {
    const needDistribution = form.getFieldValue('need_distribution');
    return (
      <Input.Group>
        <Form.Item noStyle name={['distribution', 'min_value']}>
          <InputNumber
            placeholder="Type the min value"
            disabled={mode === 'view' || !needDistribution}
            style={{ width: 155 }}
            controls={false}
          />
        </Form.Item>
        {' - '}
        <Form.Item noStyle name={['distribution', 'max_value']}>
          <InputNumber
            placeholder="Type the max value"
            disabled={mode === 'view' || !needDistribution}
            style={{ width: 155 }}
            controls={false}
          />
        </Form.Item>
      </Input.Group>
    );
  };

  const getItemRender = (item, type?: EMetricType) => {
    if (mode === 'view') {
      if (item === 'firstEvent' || item === 'secondEvent') {
        return (
          <div className={styles.viewSelect}>
            {viewData[`${item}Label`] || form.getFieldValue(item)}
          </div>
        );
      }
      if (item === 'occurrenceType') {
        return <span>&nbsp;{viewData[`${item}Label`] || form.getFieldValue(item)}&nbsp;</span>;
      }
      if (item === 'distribution') {
        return renderDistribution();
      }
      if (item === 'withinRange' || item === 'withinUnit') {
        return <div>&nbsp;{viewData[`${item}Label`] || form.getFieldValue(item)}&nbsp;</div>;
      }
      return (
        <AddEllipsisWithTooltip
          text={`\u00A0${viewData[`${item}Label`] || form.getFieldValue(item)}\u00A0`}
          dataIndex="0"
        />
      );
    }

    switch (item) {
      case 'metricType':
        return (
          <Select
            options={metricTypeOptions}
            style={{ width: '180px' }}
            onChange={(v) => {
              if (v === EMetricType.TIME_INTERVAL) {
                form.setFieldsValue({
                  calculation: ECalculationType.AVERAGE,
                  occurrenceType: EOccurrenceType.FIRST_PAIR,
                });
              } else if (v === EMetricType.SESSIONS) {
                form.setFieldsValue({
                  calculation: ECalculationType.CONVERSION_RATE,
                  occurrenceType: EOccurrenceType.SESSIONS,
                });
              }
            }}
            disabled={disabledList.includes('metricType')}
          />
        );
      case 'metricName':
        return (
          <Input
            placeholder="Type the name of the metric"
            showCount
            maxLength={256}
            style={{ marginBottom: '5px' }}
            disabled={disabledList.includes('metricName')}
          />
        );
      case 'description':
        return (
          <TextArea
            placeholder="Type the description of the metric"
            rows={4}
            showCount
            maxLength={500}
            style={{ marginBottom: '5px' }}
            disabled={disabledList.includes('description')}
          />
        );
      case 'calculation':
        return (
          <Select
            options={calculationOptions.filter(
              (x) => x.type === (type || EMetricType.TIME_INTERVAL),
            )}
            style={{ width: '180px' }}
            disabled={disabledList.includes('calculation')}
          />
        );
      case 'firstEvent':
        return renderEventItem(firstEventStatus, 'first');
      case 'secondEvent':
        return renderEventItem(secondEventStatus, 'second');
      case 'occurrenceType': {
        const filterOptions = occurrenceTypeOptions.filter(
          (x) => x.type === (type || EMetricType.TIME_INTERVAL),
        );
        return (
          <Select
            style={{ width: '165px', margin: '0 12px' }}
            options={filterOptions}
            disabled={disabledList.includes('occurrenceType')}
          />
        );
      }
      case 'withinRange':
        return (
          <InputNumber
            placeholder="Type a number"
            style={{ width: '125px', margin: '0 12px' }}
            controls={false}
            disabled={disabledList.includes('withinRange')}
          />
        );
      case 'withinUnit':
        return (
          <Select
            options={withinUnitOptions}
            style={{ width: '100px' }}
            disabled={disabledList.includes('withinUnit')}
          />
        );
      case 'pairingLogic':
        return (
          <Select
            options={pairingLogicOptions}
            style={{ width: '160px', marginBottom: '5px' }}
            disabled={disabledList.includes('pairingLogic')}
          />
        );
      case 'distribution':
        return renderDistribution();

      default:
        return '';
    }
  };

  if (mode === 'view' && metricId && !metric) return <Spin spinning={true} />;

  return (
    <div className={styles.rootContainer}>
      <Form
        form={form}
        labelAlign="left"
        labelCol={{ flex: '120px' }}
        colon={false}
        onValuesChange={(_, v) => {
          onFormValueChange && onFormValueChange(v, form);
        }}
      >
        <Form.Item name="metricType" label="Metric Type" required style={{ marginBottom: '16px' }}>
          {getItemRender('metricType')}
        </Form.Item>
        <Form.Item
          name="metricName"
          label="Metric Name"
          required
          rules={[{ validator: (rule, value) => validateMetricName(rule, value, 'metric') }]}
          style={{ marginBottom: '16px' }}
        >
          {getItemRender('metricName')}
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Description is required' }]}
          style={{ marginBottom: '16px' }}
        >
          {getItemRender('description')}
        </Form.Item>
        <Form.Item dependencies={['metricType']} noStyle>
          {({ getFieldValue }) => {
            const metricType = getFieldValue('metricType');
            return (
              <Form.Item
                name="calculation"
                label="Calculation"
                rules={[{ required: true }]}
                style={{ marginBottom: '16px' }}
              >
                {getItemRender('calculation', metricType)}
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item noStyle>
          <Form.Item
            label="Definition"
            wrapperCol={{
              style: { border: 'solid 1px #E8E8E8', padding: '10px', borderRadius: '4px' },
            }}
            required
          >
            <Form.Item noStyle>
              <div className={styles.definitionTitle}>Initial Event</div>
              <Form.Item>
                <Form.Item name="firstEvent" rules={[{ validator: validateFirstEvent }]} noStyle>
                  {getItemRender('firstEvent')}
                </Form.Item>
                {renderEventAlert(firstEventStatus, 'first')}
              </Form.Item>
            </Form.Item>
            <Form.Item noStyle>
              <div className={styles.definitionTitle}>Follow-up Event</div>
              <Form.Item>
                <Form.Item
                  name="secondEvent"
                  dependencies={['firstEvent']}
                  rules={[{ validator: validateSecondEvent }]}
                  noStyle
                >
                  {getItemRender('secondEvent')}
                </Form.Item>
                {renderEventAlert(secondEventStatus, 'second')}
              </Form.Item>
            </Form.Item>
            <Form.Item dependencies={['metricType']} noStyle>
              {({ getFieldValue }) => {
                const metricType = getFieldValue('metricType');
                if (metricType === EMetricType.TIME_INTERVAL) {
                  return (
                    <Form.Item noStyle>
                      <div className={styles.definitionTitle}>Pairing Logic</div>
                      <Form.Item name="pairingLogic" noStyle>
                        {getItemRender('pairingLogic')}
                      </Form.Item>
                      <Form.Item noStyle dependencies={['pairingLogic']}>
                        {({ getFieldValue }) => {
                          const pairingLogic = getFieldValue('pairingLogic');
                          let text;
                          if (pairingLogic === EPairType.FIRST_FIRST) {
                            text = 'first';
                          }
                          if (pairingLogic === EPairType.LAST_FIRST) {
                            text = 'last';
                          }
                          return text ? (
                            <span
                              className={styles.additionalInformation}
                              style={{ marginLeft: '12px' }}
                            >
                              {`(Pair the ${text}`}
                              <span style={{ fontStyle: 'italic' }}> initial event </span>
                              encountered before the
                              <span style={{ fontStyle: 'italic' }}> complete event </span>
                              {'occurred)'}
                            </span>
                          ) : null;
                        }}
                      </Form.Item>
                      <Form.Item style={{ borderLeft: 'solid 1px #E8E8E8', paddingLeft: '12px' }}>
                        <span>Count</span>
                        <Form.Item name="occurrenceType" noStyle>
                          {getItemRender('occurrenceType', EMetricType.TIME_INTERVAL)}
                        </Form.Item>
                        <span>in a same session</span>
                      </Form.Item>
                    </Form.Item>
                  );
                }
                if (metricType === EMetricType.SESSIONS) {
                  return (
                    <Form.Item noStyle>
                      <div className={styles.definitionTitle}>Conversion Criteria</div>
                      <Form.Item
                        style={{
                          borderLeft: 'solid 1px #E8E8E8',
                          paddingLeft: '12px',
                          marginBottom: '8px',
                        }}
                      >
                        <span>Count</span>
                        <Form.Item noStyle name="occurrenceType">
                          {getItemRender('occurrenceType', EMetricType.SESSIONS)}
                        </Form.Item>
                        <Form.Item dependencies={['occurrenceType', 'calculation']} noStyle>
                          {({ getFieldValue }) => {
                            const analyzeType = getFieldValue('occurrenceType');
                            const calculation = getFieldValue('calculation');
                            let text = '';
                            let unit = '';
                            if (analyzeType === EOccurrenceType.SESSIONS) {
                              text = 'of sessions with successful conversions';
                            } else if (analyzeType === EOccurrenceType.FIRST) {
                              text =
                                'of successful attempts counting only the first attempt in each session';
                            } else if (analyzeType === EOccurrenceType.MULTIPLE) {
                              text =
                                'of successful attempts counting all attempts within a session';
                            }
                            if (calculation === ECalculationType.CONVERSION_RATE) {
                              unit = '%';
                            } else if (calculation === ECalculationType.NUMBER) {
                              unit = 'count';
                            }
                            return text && unit ? (
                              <span className={styles.additionalInformation}>
                                {`(${unit} ${text})`}
                              </span>
                            ) : null;
                          }}
                        </Form.Item>
                      </Form.Item>
                      <Form.Item
                        style={{
                          borderLeft: 'solid 1px #E8E8E8',
                          paddingLeft: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span>Completed within</span>
                          <Form.Item
                            name="withinRange"
                            noStyle
                            rules={[{ validator: validateTime }]}
                          >
                            {getItemRender('withinRange')}
                          </Form.Item>
                          <Form.Item name="withinUnit" noStyle>
                            {getItemRender('withinUnit')}
                          </Form.Item>
                        </div>
                      </Form.Item>
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>
          </Form.Item>
          <Form.Item dependencies={['metricType']} noStyle>
            {({ getFieldValue }) => {
              const metricType = getFieldValue('metricType');
              if (metricType === EMetricType.SESSIONS) {
                return (
                  <div style={{ paddingLeft: '120px' }} className={styles.additionalInformation}>
                    Both events are in the same session.
                  </div>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form.Item>
        <Form.Item dependencies={['metricType']} noStyle>
          {({ getFieldValue }) => {
            const metricType = getFieldValue('metricType');
            if (metricType === EMetricType.TIME_INTERVAL) {
              return (
                <Form.Item
                  label="Distribution"
                  wrapperCol={{
                    style: {
                      border: 'solid 1px #E8E8E8',
                      padding: '10px',
                      borderRadius: '4px',
                      marginBottom: '24px',
                    },
                  }}
                >
                  <Form.Item noStyle name={['need_distribution']} valuePropName="checked">
                    <Checkbox
                      style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 400, fontSize: '14px' }}
                      disabled={mode === 'view' || disabledList.includes('need_distribution')}
                    >
                      Display the distribution of this metric
                    </Checkbox>
                  </Form.Item>
                  <Form.Item noStyle dependencies={['need_distribution']}>
                    {({ getFieldValue }) => {
                      const needDistribution = getFieldValue('need_distribution');
                      return (
                        <>
                          <div
                            style={{
                              marginTop: 20,
                              marginBottom: 8,
                              fontWeight: 500,
                              color: `${
                                !needDistribution ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.85)'
                              }`,
                            }}
                          >
                            Range of the distribution
                          </div>
                          <Form.Item
                            name="distribution"
                            dependencies={[
                              ['distribution', 'min_value'],
                              ['distribution', 'max_value'],
                            ]}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator() {
                                  // the value can be null or undefined
                                  const minValue =
                                    getFieldValue(['distribution', 'min_value']) ?? null;
                                  const maxValue =
                                    getFieldValue(['distribution', 'max_value']) ?? null;
                                  if (!needDistribution) {
                                    return Promise.resolve();
                                  }
                                  if (minValue === null || maxValue === null) {
                                    return Promise.reject(new Error('Distribution is required'));
                                  }
                                  if (minValue < maxValue) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error('Min value must less than max value!'),
                                  );
                                },
                              }),
                            ]}
                          >
                            {getItemRender('distribution')}
                          </Form.Item>
                        </>
                      );
                    }}
                  </Form.Item>
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>
      </Form>
    </div>
  );
};

export default React.memo(MetricDetail);
